import { DateTime } from 'luxon'
import { default as qs } from 'qs'
import { version } from '../../../'
import { default as YAML } from 'yaml'
import { default as pMap } from 'p-map'
import { toXmlValue } from '../utils/xml'
import { encode, decode } from '../../encoding'
import { negotiateContent } from '../utils/http'
import { normalizeError } from '../utils/errors'
import { default as Hooks } from '@poppinss/hooks'
import { joi } from '@nhtio/lucid-resourceful/joi'
import { stripUndefinedValuesFromObject } from '../../utils'
import { encode as encodeJoi } from '@nhtio/lucid-resourceful/joi'
import { isObject, isArray, isResourcefulModel } from '../../type_guards'
import {
  E_UNRESOLVABLE_MODEL,
  E_ROUTE_NOT_FOUND,
  E_RELATIONSHIP_NOT_FOUND_EXCEPTION,
  E_RECORD_NOT_FOUND_EXCEPTION,
  E_UNSYNCABLE_RELATIONSHIP_EXCEPTION,
  E_INVALID_RELATIONSHIP_EXCEPTION,
} from '@nhtio/lucid-resourceful/errors'
import type { ElementCompact } from 'xml-js'
import type { HttpContext } from '@adonisjs/core/http'
import type { AnySchema } from '@nhtio/lucid-resourceful/joi'
import type { ApplicationService } from '@adonisjs/core/types'
import type { ResourcefulRouterModelServiceOptionsService } from '../services/resourceful_router_model_options_service'
import type {
  ResourcefulModel,
  ResourcefulIndexResult,
  ServableResourcefulIndexResult,
  ResourcefulIndexResponse,
  ResourceResourcefulRecordResponse,
  ResourcefulQueryScopeCallback,
  ResourcefulPayloadSchemaGetter,
  ResourcefulPolicy,
  ResourcefulIndexResponseHook,
  ResourceResourcefulRecordResponseHook,
  ResourcefulRouterMutatorOptions,
} from '@nhtio/lucid-resourceful/types'

const encoder = (_key: string, value: any) => {
  if (value instanceof Date) {
    return DateTime.fromJSDate(value).toISO()
  }
  if (value instanceof DateTime) {
    return value.toISO()
  }
  if (typeof value === 'bigint') {
    return value.toString()
  }
  return value
}

const makeEncodable = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map((item) => makeEncodable(item))
  }
  if (data instanceof Date) {
    return DateTime.fromJSDate(data).toISO()
  }
  if (data instanceof DateTime) {
    return data.toISO()
  }
  if (isObject(data)) {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [key, makeEncodable(value)])
    )
  }
  return data
}

const baseIndexRequestSchema = joi
  .object({
    aggregations: joi
      .object()
      .pattern(
        joi.string().required(),
        joi
          .array()
          .items(
            joi
              .string()
              .valid('avg', 'min', 'max', 'sum', 'countDistinct', 'sumDistinct', 'avgDistinct')
          )
          .unique()
          .required()
      )
      .optional(),
    filter: joi.string().allow('', null).default(''),
    page: joi.number().integer().min(1).default(1),
    perPage: joi.number().integer().min(1).max(100).default(20),
    sort: joi
      .object()
      .pattern(joi.string().required(), joi.string().valid('asc', 'desc').default('asc'))
      .default({ id: 'asc' }),
  })
  .unknown(false)

const preProcessingIndexRequestSchema = baseIndexRequestSchema.concat(
  joi.object({
    fields: joi.alternatives(
      joi
        .string()
        .pattern(/^[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)?(,[a-zA-Z0-9_]+(\.[a-zA-Z0-9_]+)?)*$/)
        .allow('', null)
        .default(''),
      joi.array().items(joi.string()).unique().optional()
    ),
  })
)

const postProcessingIndexRequestSchema = baseIndexRequestSchema.concat(
  joi.object({
    fields: joi.array().items(joi.string()).unique().optional(),
  })
)

interface ModelRelationshipSyncPayload {
  ruid: string | number
  [key: string]: any
}

const modelRelationshipSyncPayloadSchema = joi
  .object<ModelRelationshipSyncPayload>({
    ruid: joi
      .alternatives()
      .try(joi.string().required(), joi.number().integer().required())
      .required(),
  })
  .pattern(joi.string().required(), joi.any())
  .unknown(true)
const modelRelationshipSyncPayloadSchemaArray = joi
  .array()
  .items(modelRelationshipSyncPayloadSchema)

export type ResourcefulModelControllerHooks = {
  index: [
    [ResourcefulIndexResponse, HttpContext],
    [error: Error | null, ResourcefulIndexResponse, HttpContext],
  ]
  create: [
    [ResourceResourcefulRecordResponse, HttpContext],
    [error: Error | null, ResourceResourcefulRecordResponse, HttpContext],
  ]
  read: [
    [ResourceResourcefulRecordResponse, HttpContext],
    [error: Error | null, ResourceResourcefulRecordResponse, HttpContext],
  ]
  readRelated: [
    [ResourcefulIndexResponse, HttpContext],
    [error: Error | null, ResourcefulIndexResponse, HttpContext],
  ]
  update: [
    [ResourceResourcefulRecordResponse, HttpContext],
    [error: Error | null, ResourceResourcefulRecordResponse, HttpContext],
  ]
}

export class ResourcefulModelController {
  readonly #subpath: string
  readonly #model: Promise<ResourcefulModel | E_UNRESOLVABLE_MODEL>
  readonly #options: ResourcefulRouterModelServiceOptionsService
  readonly #headers: {
    [key: string]: string
  }
  readonly #mutators: Hooks<ResourcefulModelControllerHooks>
  readonly #scopeRestrictors: ResourcefulQueryScopeCallback[]
  readonly #payloadRestrictors: ResourcefulPayloadSchemaGetter[]
  get options() {
    return this.#options
  }

  constructor(
    subpath: string,
    model: Promise<ResourcefulModel | E_UNRESOLVABLE_MODEL>,
    options: ResourcefulRouterModelServiceOptionsService,
    headers: {
      [key: string]: string
    },
    groupResourcefulQueryScopeCallbacks?: ResourcefulQueryScopeCallback[],
    groupResourcefulPayloadSchemaGetters?: ResourcefulPayloadSchemaGetter[],
    groupResourcefulRouterMutatorOptions?: ResourcefulRouterMutatorOptions,
    groupResourcefulPolicies?: ResourcefulPolicy[]
  ) {
    this.#subpath = subpath
    this.#model = model
    this.#options = options
    this.#headers = headers
    this.#mutators = new Hooks<ResourcefulModelControllerHooks>()
    this.#scopeRestrictors = [
      ...(groupResourcefulQueryScopeCallbacks || []),
      ...(this.#options.scopeRestrictors || []),
    ]
    this.#payloadRestrictors = [
      ...(groupResourcefulPayloadSchemaGetters || []),
      ...(this.#options.payloadRestrictors || []),
    ]
    if (isObject(groupResourcefulRouterMutatorOptions)) {
      if (
        'index' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.index)
      ) {
        groupResourcefulRouterMutatorOptions.index.forEach((hook) => {
          this.#mutators.add('index', hook as ResourcefulIndexResponseHook)
        })
      }
      if (
        'create' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.create)
      ) {
        groupResourcefulRouterMutatorOptions.create.forEach((hook) => {
          this.#mutators.add('create', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if (
        'read' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.read)
      ) {
        groupResourcefulRouterMutatorOptions.read.forEach((hook) => {
          this.#mutators.add('read', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if (
        'readRelated' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.readRelated)
      ) {
        groupResourcefulRouterMutatorOptions.readRelated.forEach((hook) => {
          this.#mutators.add('readRelated', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if (
        'update' in groupResourcefulRouterMutatorOptions &&
        isArray(groupResourcefulRouterMutatorOptions.update)
      ) {
        groupResourcefulRouterMutatorOptions.update.forEach((hook) => {
          this.#mutators.add('update', hook as ResourceResourcefulRecordResponseHook)
        })
      }
    }
    if (isObject(this.#options.mutators)) {
      if ('index' in this.#options.mutators && isArray(this.#options.mutators.index)) {
        this.#options.mutators.index.forEach((hook) => {
          this.#mutators.add('index', hook as ResourcefulIndexResponseHook)
        })
      }
      if ('create' in this.#options.mutators && isArray(this.#options.mutators.create)) {
        this.#options.mutators.create.forEach((hook) => {
          this.#mutators.add('create', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if ('read' in this.#options.mutators && isArray(this.#options.mutators.read)) {
        this.#options.mutators.read.forEach((hook) => {
          this.#mutators.add('read', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if ('readRelated' in this.#options.mutators && isArray(this.#options.mutators.readRelated)) {
        this.#options.mutators.readRelated.forEach((hook) => {
          this.#mutators.add('readRelated', hook as ResourceResourcefulRecordResponseHook)
        })
      }
      if ('update' in this.#options.mutators && isArray(this.#options.mutators.update)) {
        this.#options.mutators.update.forEach((hook) => {
          this.#mutators.add('update', hook as ResourceResourcefulRecordResponseHook)
        })
      }
    }
    if (groupResourcefulPolicies) {
      groupResourcefulPolicies.forEach((policy) => {
        if (policy.scope) {
          this.#scopeRestrictors.push(policy.scope)
        }
        if (policy.payload) {
          this.#payloadRestrictors.push(policy.payload)
        }
        if (isObject(policy.mutators)) {
          if ('index' in policy.mutators && isArray(policy.mutators.index)) {
            policy.mutators.index.forEach((hook) => {
              this.#mutators.add('index', hook as ResourcefulIndexResponseHook)
            })
          }
          if ('create' in policy.mutators && isArray(policy.mutators.create)) {
            policy.mutators.create.forEach((hook) => {
              this.#mutators.add('create', hook as ResourceResourcefulRecordResponseHook)
            })
          }
          if ('read' in policy.mutators && isArray(policy.mutators.read)) {
            policy.mutators.read.forEach((hook) => {
              this.#mutators.add('read', hook as ResourceResourcefulRecordResponseHook)
            })
          }
          if ('readRelated' in policy.mutators && isArray(policy.mutators.readRelated)) {
            policy.mutators.readRelated.forEach((hook) => {
              this.#mutators.add('readRelated', hook as ResourceResourcefulRecordResponseHook)
            })
          }
          if ('update' in policy.mutators && isArray(policy.mutators.update)) {
            policy.mutators.update.forEach((hook) => {
              this.#mutators.add('update', hook as ResourceResourcefulRecordResponseHook)
            })
          }
        }
      })
    }
  }

  async resolveRequestRequirements(ctx: HttpContext) {
    const [model, { default: app }] = await Promise.all([
      this.#model,
      import('@adonisjs/core/services/app'),
    ])
    const serveAs = negotiateContent(ctx)
    const method = ctx.request.method() || 'GET'
    const url = ctx.request.url() || '/'
    if (model instanceof E_UNRESOLVABLE_MODEL) {
      throw new E_ROUTE_NOT_FOUND([method, url])
    }
    const respond = (status: number, body: any, headers: { [key: string]: string } = {}) => {
      ctx.response.status(status)
      // apply macro-level headers
      Object.entries(this.#headers).forEach(([key, value]) => {
        ctx.response.header(key, value)
      })
      // apply controller-level headers
      Object.entries(this.#options.headers).forEach(([key, value]) => {
        ctx.response.header(key, value)
      })
      // apply request-level headers
      Object.entries(headers).forEach(([key, value]) => {
        ctx.response.header(key, value)
      })
      // if response is requested as encoded, return as encoded plaintext
      if (ctx.request.header('x-resourceful-response-encoded') === '1') {
        ctx.response.header(
          'Content-Type',
          `application/vnd.resourceful.structured; coding="zlib+base64"; ver="${version}"`
        )
        const encodable = makeEncodable(body)
        ctx.response.send(encode(encodable))
        return
      }
      // apply content-type headers
      switch (serveAs) {
        case 'yaml':
          ctx.response.header('Content-Type', 'application/x-yaml')
          break
        default:
          ctx.response.header('Content-Type', 'application/json')
          break
      }
      if ('undefined' !== typeof body && 204 !== status) {
        switch (serveAs) {
          case 'yaml':
            ctx.response.send(YAML.stringify(body, encoder))
            break
          default:
            ctx.response.send(JSON.stringify(body, encoder))
            break
        }
      }
    }
    return {
      model,
      app,
      prefix: this.#subpath,
      serveAs,
      method,
      url,
      respond,
    }
  }

  async #processIndexRequest(requested: any, ctx: HttpContext) {
    // Normalize indexed-object fields (qs arrayLimit shim):
    // When qs parses >20 indexed array params (e.g. fields[21]=x), it produces
    // a plain object {"0":"id","1":"status",...} instead of an array.
    // Convert it to a sorted array before Joi validation.
    if ('fields' in requested && isObject(requested.fields) && !Array.isArray(requested.fields)) {
      const keys = Object.keys(requested.fields)
      if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
        requested = {
          ...requested,
          fields: keys.sort((a, b) => Number(a) - Number(b)).map((k) => requested.fields[k]),
        }
      }
    }
    const { error: preprocessingError, value: prepared } = preProcessingIndexRequestSchema.validate(
      requested,
      {
        abortEarly: false,
        convert: true,
      }
    )
    if (preprocessingError) {
      throw preprocessingError
    }
    if ('fields' in prepared && 'string' === typeof prepared.fields) {
      prepared.fields = prepared.fields
        .split(',')
        .map((str: string) => str.trim())
        .filter((str: string) => str.length > 0)
    }
    const { error: postprocessingError, value: processed } =
      postProcessingIndexRequestSchema.validate(prepared, {
        abortEarly: false,
        convert: true,
      })
    if (postprocessingError) {
      throw postprocessingError
    }
    const sort = Object.entries(processed.sort) as [string, 'asc' | 'desc'][]
    const encoded = encode(requested)
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const response = await model.$onResourcefulIndex(
      processed.filter,
      processed.page,
      processed.perPage,
      processed.fields || null,
      sort,
      ctx,
      app,
      this.#scopeRestrictors,
      processed.aggregations
    )
    let fullUrl = ctx.request.completeUrl(false)
    if (ctx.params.encoded) {
      fullUrl = fullUrl.replace(/\/s\/.*$/, '/')
    }
    while (!fullUrl.endsWith('/')) {
      fullUrl += '/'
    }
    const metaUrl = `${fullUrl}$meta.index`
    let linkedUrl = ctx.params.encoded
      ? `${fullUrl}${qs.stringify(requested, { addQueryPrefix: true })}`
      : `${fullUrl}s/${encodeURIComponent(encoded)}`
    const linkedUrlKey = ctx.params.encoded ? 'x-full-url' : 'x-shortcut-url'
    const ret: ResourcefulIndexResponse = {
      records: response.records,
      total: response.total,
      page: response.page,
      perPage: response.perPage,
      aggregations: response.aggregations,
    }
    const runner = this.#mutators.runner('index')
    await runner.run(ret, ctx)
    return respond(200, ret, {
      'x-metadata-url': metaUrl,
      [linkedUrlKey]: linkedUrl,
    })
  }

  // GET /:subpath/
  async serveModelIndex(ctx: HttpContext) {
    return await this.#processIndexRequest(ctx.request.qs(), ctx)
  }

  // GET /:subpath/s/:encoded
  async serveModelShortcut(ctx: HttpContext) {
    const { encoded } = ctx.params
    const decoded = decode(decodeURIComponent(encoded))
    return await this.#processIndexRequest(decoded, ctx)
  }

  // GET /:subpath/$meta.index
  async serveModelIndexMeta(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const schema = await model.$asOpenApiSchemaObject(ctx, app, 'read')
    const filterable = schema['x-resourceful-filterable'] || []
    const sortable = schema['x-resourceful-sortable'] || []
    const aggregatable = schema['x-resourceful-aggregatable'] || []
    return respond(
      200,
      JSON.parse(
        JSON.stringify(
          {
            selectable: schema.properties,
            filterable,
            sortable,
            aggregatable,
          },
          encoder
        )
      )
    )
  }

  async #getWriteValidationSchema(
    ctx: HttpContext,
    model: ResourcefulModel,
    app: ApplicationService,
    forUpdate: boolean = false
  ) {
    return await model.$getResourcefulWriteValidationSchema(ctx, app, forUpdate)
  }

  // POST /:subpath/
  async serveModelCreate(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const { validator } = await this.#getWriteValidationSchema(ctx, model, app)
    const { error, value: validated } = validator.validate(ctx.request.all(), {
      abortEarly: false,
      convert: true,
    })
    if (error) {
      throw normalizeError(error)
    }
    let fullUrl = ctx.request.completeUrl(false)
    while (!fullUrl.endsWith('/')) {
      fullUrl += '/'
    }
    const metaUrl = `${fullUrl}$meta.create`
    try {
      const ret: ResourceResourcefulRecordResponse<typeof model> = await model.$onResourcefulCreate(
        validated,
        ctx,
        app,
        this.#payloadRestrictors
      )
      const runner = this.#mutators.runner('create')
      await runner.run(ret, ctx)
      return respond(201, ret, {
        'x-metadata-url': metaUrl,
      })
    } catch (e) {
      throw normalizeError(e)
    }
  }

  // GET /:subpath/$meta.create
  async serveModelCreateMeta(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const { schema, validator } = await this.#getWriteValidationSchema(ctx, model, app)
    const ret = {
      schema: schema.properties,
      joiSchema: encodeJoi(validator),
      joiSchemaDescription: validator.describe(),
    }
    return respond(200, ret)
  }

  // GET /:subpath/:id
  async serveModelRead(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const id = ctx.params.id
    const ret: ResourceResourcefulRecordResponse<typeof model> = await model.$onResourcefulRead(
      id,
      ctx,
      app,
      this.#scopeRestrictors
    )
    const runner = this.#mutators.runner('read')
    await runner.run(ret, ctx)
    return respond(200, ret)
  }

  async #processModelReadRelatedRequest(requested: any, ctx: HttpContext) {
    const { id, relationship } = ctx.params
    // Normalize indexed-object fields (qs arrayLimit shim)
    if ('fields' in requested && isObject(requested.fields) && !Array.isArray(requested.fields)) {
      const keys = Object.keys(requested.fields)
      if (keys.length > 0 && keys.every((k) => /^\d+$/.test(k))) {
        requested = {
          ...requested,
          fields: keys.sort((a, b) => Number(a) - Number(b)).map((k) => requested.fields[k]),
        }
      }
    }
    const { error: preprocessingError, value: prepared } = preProcessingIndexRequestSchema.validate(
      requested,
      {
        abortEarly: false,
        convert: true,
      }
    )
    if (preprocessingError) {
      throw preprocessingError
    }
    if ('fields' in prepared && 'string' === typeof prepared.fields) {
      prepared.fields = prepared.fields
        .split(',')
        .map((str: string) => str.trim())
        .filter((str: string) => str.length > 0)
    }
    const { error: postprocessingError, value: processed } =
      postProcessingIndexRequestSchema.validate(prepared, {
        abortEarly: false,
        convert: true,
      })
    if (postprocessingError) {
      throw postprocessingError
    }
    const sort = Object.entries(processed.sort) as [string, 'asc' | 'desc'][]
    const encoded = encode(requested)
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const response = await model.$onResourcefulReadRelationship(
      id,
      relationship,
      processed.filter,
      processed.page,
      processed.perPage,
      processed.fields || null,
      sort,
      ctx,
      app,
      this.#scopeRestrictors,
      processed.aggregations
    )
    let fullUrl = ctx.request.completeUrl(false)
    if (ctx.params.encoded) {
      fullUrl = fullUrl.replace(/\/s\/.*$/, '/')
    }
    while (!fullUrl.endsWith('/')) {
      fullUrl += '/'
    }
    const metaUrl = `${fullUrl}$meta.index`
    let linkedUrl = ctx.params.encoded
      ? `${fullUrl}${qs.stringify(requested, { addQueryPrefix: true })}`
      : `${fullUrl}s/${encodeURIComponent(encoded)}`
    const linkedUrlKey = ctx.params.encoded ? 'x-full-url' : 'x-shortcut-url'
    const ret: ResourcefulIndexResponse = {
      records: response.records,
      total: response.total,
      page: response.page,
      perPage: response.perPage,
      aggregations: response.aggregations,
    }
    const runner = this.#mutators.runner('readRelated')
    await runner.run(ret, ctx)
    return respond(200, ret, {
      'x-metadata-url': metaUrl,
      [linkedUrlKey]: linkedUrl,
    })
  }

  // GET /:subpath/:id/:relationship
  async serveModelReadRelated(ctx: HttpContext) {
    return await this.#processModelReadRelatedRequest(ctx.request.qs(), ctx)
  }

  // GET /:subpath/:id/:relationship/s/:encoded
  async serveModelReadRelatedShortcut(ctx: HttpContext) {
    const { encoded } = ctx.params
    const decoded = decode(decodeURIComponent(encoded))
    return await this.#processModelReadRelatedRequest(decoded, ctx)
  }

  // GET /:subpath/:id/:relationship/$meta.index
  async serveModelReadRelatedMeta(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const { relationship } = ctx.params
    const resourcefulRelationshipDefinition = model.$resourcefulRelationships.get(relationship)
    if (!resourcefulRelationshipDefinition) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const lucidRelationshipDefinition = model.$getRelation(relationship)
    if (!lucidRelationshipDefinition) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    switch (lucidRelationshipDefinition.type) {
      case 'hasOne':
      case 'belongsTo':
        throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const relatedResourcefulModel = resourcefulRelationshipDefinition.relatedModel()
    if (!isResourcefulModel(relatedResourcefulModel)) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const schema = await relatedResourcefulModel.$asOpenApiSchemaObject(ctx, app, 'read')
    const selectable = schema.properties || {}
    const filterable = schema['x-resourceful-filterable'] || []
    const sortable = schema['x-resourceful-sortable'] || []
    const aggregatable = schema['x-resourceful-aggregatable'] || []
    return respond(200, {
      selectable,
      filterable,
      sortable,
      aggregatable,
    })
  }

  // PUT /:subpath (with id in the payload)
  // PUT /:subpath/:id
  // PATCH /:subpath/:id
  async serveModelUpdate(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const id = ctx.params.id || ctx.request.input(model.primaryKey)
    const { validator } = await this.#getWriteValidationSchema(ctx, model, app, true)
    const { error, value: validated } = validator.validate(
      stripUndefinedValuesFromObject({
        ...ctx.request.all(),
        [model.primaryKey]: undefined,
      }),
      {
        abortEarly: false,
        convert: true,
      }
    )
    if (error) {
      throw normalizeError(error)
    }
    let fullUrl = ctx.request.completeUrl(false)
    while (!fullUrl.endsWith('/')) {
      fullUrl += '/'
    }
    const metaUrl = `${fullUrl}$meta.update`
    try {
      const ret: ResourceResourcefulRecordResponse<typeof model> = await model.$onResourcefulUpdate(
        id,
        validated,
        ctx,
        app,
        {
          queryScopeCallbacks: this.#scopeRestrictors,
          payloadValidationSchemas: this.#payloadRestrictors,
        }
      )
      const runner = this.#mutators.runner('update')
      await runner.run(ret, ctx)
      return respond(201, ret, {
        'x-metadata-url': metaUrl,
      })
    } catch (e) {
      throw normalizeError(e)
    }
  }

  // GET /:subpath/$meta.update
  async serveModelUpdateMeta(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const { schema, validator } = await this.#getWriteValidationSchema(ctx, model, app, true)
    const ret = {
      schema: schema.properties,
      joiSchema: encodeJoi(validator),
      joiSchemaDescription: validator.describe(),
    }
    return respond(200, ret)
  }

  async #getAndValidateBulkUpdatePayload(
    input: unknown,
    ctx: HttpContext,
    model: ResourcefulModel,
    app: ApplicationService
  ) {
    const { validator } = await this.#getWriteValidationSchema(ctx, model, app, true)
    const { error, value: validated } = validator.validate(input, {
      abortEarly: false,
      convert: true,
    })
    if (error) {
      throw normalizeError(error)
    }
    return validated
  }

  async #processModelBulkUpdateRequestByFilter(filter: string | null, ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const payload = await this.#getAndValidateBulkUpdatePayload(
      stripUndefinedValuesFromObject({
        ...ctx.request.all(),
        filter: undefined,
      }),
      ctx,
      model,
      app
    )
    try {
      await model.$onResourcefulBulkUpdate(filter, payload, ctx, app, {
        queryScopeCallbacks: this.#scopeRestrictors,
        payloadValidationSchemas: this.#payloadRestrictors,
      })
      return respond(204, undefined)
    } catch (e) {
      throw normalizeError(e)
    }
  }

  async #processModelBulkUpdateRequestById(ids: string, ctx: HttpContext) {
    const { error: preprocessingError, value: prepared } = joi
      .string()
      .pattern(/^[a-zA-Z0-9_,\s]+$/)
      .required()
      .validate(ids, {
        abortEarly: false,
        convert: true,
      })
    if (preprocessingError) {
      throw preprocessingError
    }
    const parsedIds = prepared.split(',').map((id: string) => id.trim())
    const { error: postprocessingError, value: processed } = joi
      .array()
      .items(joi.alternatives(joi.number().integer(), joi.string()))
      .unique()
      .min(1)
      .validate(parsedIds, {
        abortEarly: false,
        convert: true,
      }) as { error?: Error; value: (string | number)[] }
    if (postprocessingError) {
      throw postprocessingError
    }
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const payload = await this.#getAndValidateBulkUpdatePayload(
      stripUndefinedValuesFromObject({
        ...ctx.request.all(),
        ids: undefined,
      }),
      ctx,
      model,
      app
    )
    try {
      await model.$onResourcefulBulkUpdateByUid(processed, payload, ctx, app, {
        queryScopeCallbacks: this.#scopeRestrictors,
        payloadValidationSchemas: this.#payloadRestrictors,
      })
      return respond(204, undefined)
    } catch (e) {
      throw normalizeError(e)
    }
  }

  // PUT /:subpath/$bulk (with ids or filters in the query)
  // PUT /:subpath/$bulk/:ids
  // PATCH /:subpath/$bulk (with ids or filters in the query)
  // PATCH /:subpath/$bulk/:ids
  async serveModelBulkUpdate(ctx: HttpContext) {
    const ids = ctx.params.ids || ctx.request.qs().ids
    if (ids) {
      return await this.#processModelBulkUpdateRequestById(ids, ctx)
    }
    return await this.#processModelBulkUpdateRequestByFilter(ctx.request.qs().filter || null, ctx)
  }

  // GET /:subpath/$meta.$bulk.update
  async serveModelBulkUpdateMeta(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    const readable = await model.$asOpenApiSchemaObject(ctx, app, 'read')
    const schema = await model.$asOpenApiSchemaObject(ctx, app, 'write')
    const asResourceful = await model.$getAsResourcefulForContext(ctx, app)
    const modelJoiSchema =
      await ResourcefulModelController.getResourcefulModelCreatePayloadValidationSchema(
        model,
        ctx,
        app
      )
    const modelUpdateSchema = joi.object(
      Object.assign(
        {},
        ...Object.keys(schema.properties!)
          .filter((p) => p !== model.primaryKey)
          .map((creatablePropertyKey) => {
            const returnable = { [creatablePropertyKey]: joi.any() }
            const prop = asResourceful.properties[creatablePropertyKey]
            if (prop) {
              const validator = prop.validator
              if (validator) {
                returnable[creatablePropertyKey] = validator
              }
            }
            try {
              const extracted = modelJoiSchema.extract(creatablePropertyKey)
              if (extracted) {
                returnable[creatablePropertyKey] =
                  returnable[creatablePropertyKey].concat(extracted)
              }
            } catch {
              // noop
            }
            return returnable
          })
      )
    )
    const filterable = readable['x-resourceful-filterable'] || []
    const modelSchema = Object.fromEntries(
      Object.entries(schema.properties || {}).filter(
        ([p, d]: [string, any]) =>
          p !== model.primaryKey && !d['x-resourceful-autoupdated'] && !d['x-resourceful-immutable']
      )
    )
    const ret = {
      schema: modelSchema,
      filterable: JSON.parse(JSON.stringify(filterable, encoder)),
      joiSchema: encodeJoi(modelUpdateSchema),
      joiSchemaDescription: modelUpdateSchema.describe(),
    }
    return respond(200, ret)
  }

  // PUT /:subpath/:id/:relationship
  // PATCH /:subpath/:id/:relationship
  async serveModelSyncRelated(ctx: HttpContext) {
    const payload = Object.values(ctx.request.all())
    const { error, value } = modelRelationshipSyncPayloadSchemaArray.validate(payload, {
      abortEarly: false,
      convert: true,
    })
    if (error) {
      throw normalizeError(error)
    }
    const { model, respond } = await this.resolveRequestRequirements(ctx)
    const { id, relationship } = ctx.params
    // @ts-ignore
    const instance = await model.query().where(model.primaryKey, id).first()
    if (!instance) {
      throw new E_RECORD_NOT_FOUND_EXCEPTION()
    }
    const resourcefulRelationshipDefinition = model.$resourcefulRelationships.get(relationship)
    if (!resourcefulRelationshipDefinition) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    const lucidRelationshipDefinition = model.$getRelation(relationship)
    if (!lucidRelationshipDefinition) {
      throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
    }
    switch (lucidRelationshipDefinition.type) {
      case 'hasOne':
      case 'belongsTo':
        throw new E_RELATIONSHIP_NOT_FOUND_EXCEPTION()
      case 'hasManyThrough':
        throw new E_UNSYNCABLE_RELATIONSHIP_EXCEPTION()
    }
    if (lucidRelationshipDefinition.type === 'manyToMany') {
      const syncable: { [key: string]: { [key: string]: any } } = Object.assign(
        {},
        ...value.map((v) => ({
          [v.ruid.toString()]: stripUndefinedValuesFromObject({
            ...v,
            ruid: undefined,
          }),
        }))
      )
      try {
        await instance.related(relationship).sync(syncable)
      } catch (e) {
        throw normalizeError(e)
      }
    }
    if (lucidRelationshipDefinition.type === 'hasMany') {
      // in order to ensure that model hooks are run, we will load the relevant related models which need to be synced
      // and then update them one by one
      const foreignKey = (lucidRelationshipDefinition as any).options.foreignKey
      if (!foreignKey) {
        throw new E_INVALID_RELATIONSHIP_EXCEPTION(
          `HasMany relationship '${relationship}' missing foreignKey`
        )
      }
      const relatedModel = resourcefulRelationshipDefinition.relatedModel()
      const idsToInclude = value.map((v) => v.ruid)
      const [relatedModelInstancesEnclude, relatedModelInstancesToInclude] = await Promise.all([
        instance.related(relationship).query().whereNotIn(relatedModel.primaryKey, idsToInclude),
        // @ts-ignore
        relatedModel.query().whereIn(relatedModel.primaryKey, idsToInclude),
      ])
      const relatedModelInstancesEncludeResults = await pMap(
        relatedModelInstancesEnclude as InstanceType<typeof relatedModel>[],
        async (relatedInstance) => {
          relatedInstance.merge({
            [foreignKey]: null,
          })
          try {
            await relatedInstance.save()
          } catch (e) {
            return e
          }
        },
        { concurrency: 1, stopOnError: false }
      )
      const relatedModelInstancesEncludeResultsErr = relatedModelInstancesEncludeResults.filter(
        (result) => 'undefined' !== typeof result
      )
      if (relatedModelInstancesEncludeResultsErr.length > 0) {
        throw relatedModelInstancesEncludeResultsErr[0]
      }
      const relatedModelInstancesToIncludeResults = await pMap(
        relatedModelInstancesToInclude as InstanceType<typeof relatedModel>[],
        async (relatedInstance) => {
          relatedInstance.merge({
            [foreignKey]: instance.$getAttribute(model.primaryKey),
          })
          try {
            await relatedInstance.save()
          } catch (e) {
            return e
          }
        },
        { concurrency: 1, stopOnError: false }
      )
      const relatedModelInstancesToIncludeResultsErr = relatedModelInstancesToIncludeResults.filter(
        (result) => 'undefined' !== typeof result
      )
      if (relatedModelInstancesToIncludeResultsErr.length > 0) {
        throw relatedModelInstancesToIncludeResultsErr[0]
      }
    }
    return respond(204, undefined)
  }

  // DELETE /:subpath/:id
  async serveModelDelete(ctx: HttpContext) {
    const { model, app, respond } = await this.resolveRequestRequirements(ctx)
    try {
      await model.$onResourcefulDelete(ctx.params.id, ctx, app, this.#scopeRestrictors)
      return respond(204, undefined)
    } catch (e) {
      throw normalizeError(e)
    }
  }

  static resourcefulIndexResultToConsumable(
    result: ResourcefulIndexResult
  ): ServableResourcefulIndexResult {
    return stripUndefinedValuesFromObject({
      ...result,
      countQuery: undefined,
      recordsQuery: undefined,
    })
  }

  static resourcefulIndexResultToXmlConsumable(result: ResourcefulIndexResult) {
    const servable: ServableResourcefulIndexResult =
      ResourcefulModelController.resourcefulIndexResultToConsumable(result)
    const ret: ElementCompact = stripUndefinedValuesFromObject({
      _declaration: { _attributes: { version: '1.0', encoding: 'utf-8' } },
      results: {
        _attributes: {
          total: servable.total,
          page: servable.page,
          perPage: servable.perPage,
        },
        records: servable.records.map((record: ResourcefulIndexResult['records'][number]) =>
          toXmlValue(record)
        ),
      },
      aggregations:
        Object.keys(servable.aggregations).length === 0
          ? undefined
          : Object.entries(servable.aggregations).map(([key, aggs]) =>
              stripUndefinedValuesFromObject({
                [key]: {
                  _attributes: aggs,
                },
              })
            ),
    })
    return ret
  }

  static async getResourcefulModelCreatePayloadValidationSchema(
    model: ResourcefulModel,
    ctx: HttpContext,
    app: ApplicationService
  ) {
    const schemas: AnySchema[] = []
    await pMap(
      model.$resourcefulPayloadValidationSchemaBuilders.create,
      async (builder) => {
        const schema = await builder(ctx, app)
        if (schema) {
          schemas.push(schema)
        }
      },
      { concurrency: 1 }
    )
    let ret = joi.any()
    schemas.forEach((schema) => {
      ret = ret.concat(schema)
    })
    return ret
  }
}
